/* ============================================================
   cc-motion.js — Lenis smooth scroll + GSAP reveal system.
   Loaded in theme.liquid after the CDN scripts.
   Skipped under prefers-reduced-motion: reduce.

   Exposes:
     window.__ccLenis         — Lenis instance (or null)
     window.__ccReinitMotion  — re-init GSAP + reveals after SPA swap
   ============================================================ */

(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var lenis = null;
  var revealObserver = null;
  var gsapReady = false;

  /* ---------- Lenis smooth scroll ---------- */
  if (!reduced && typeof Lenis !== 'undefined') {
    lenis = new Lenis({
      duration: 1.05,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true,
      wheelMultiplier: 0.95,
      touchMultiplier: 1.3
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
  }

  /* ---------- GSAP one-time setup (ticker + Lenis sync) ---------- */
  function bootGSAP() {
    if (gsapReady) return;
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;
    gsapReady = true;

    gsap.registerPlugin(ScrollTrigger);

    if (lenis) {
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
      gsap.ticker.lagSmoothing(0);
    }
  }

  /* ---------- GSAP scroll-driven animations (re-callable) ---------- */
  function setupScrollAnimations() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

    /* Parallax on [data-parallax] elements */
    document.querySelectorAll('[data-parallax]').forEach(function (el) {
      var strength = parseFloat(el.getAttribute('data-parallax') || '0.12');
      gsap.to(el, {
        yPercent: -strength * 100,
        ease: 'none',
        scrollTrigger: {
          trigger: el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1.2
        }
      });
    });

    /* Section number scrub on [data-section-num] */
    document.querySelectorAll('[data-section-num]').forEach(function (el) {
      gsap.from(el, {
        yPercent: 30,
        opacity: 0,
        ease: 'none',
        scrollTrigger: {
          trigger: el,
          start: 'top 92%',
          end: 'top 40%',
          scrub: 1.0
        }
      });
    });

    /* Refresh after fonts load */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { ScrollTrigger.refresh(); });
    }
  }

  /* ---------- Reveal observer (re-callable) ---------- */
  function initReveals() {
    if (revealObserver) revealObserver.disconnect();
    revealObserver = null;

    var revealEls = document.querySelectorAll('.reveal:not(.is-in), .chisel-reveal:not(.is-in)');
    if (!revealEls.length) return;

    if (reduced) {
      revealEls.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: '0px 0px -8% 0px' }
    );
    revealEls.forEach(function (el) { revealObserver.observe(el); });
  }

  /* ---------- Initial boot ---------- */
  bootGSAP();
  setupScrollAnimations();
  initReveals();

  /* ---------- Public API for page transitions ---------- */
  window.__ccLenis = lenis;

  window.__ccReinitMotion = function () {
    /* Kill stale ScrollTrigger instances from old DOM */
    if (typeof ScrollTrigger !== 'undefined') {
      ScrollTrigger.getAll().forEach(function (st) { st.kill(); });
    }
    /* Re-create scroll-driven animations for new DOM */
    setupScrollAnimations();
    /* Re-observe new reveal elements */
    initReveals();

    /* ── Re-trigger hero reveal (SPA swap delivers fresh DOM without is-in) ── */
    var heroBlock = document.querySelector('[data-cc-hero-block]');
    if (heroBlock) {
      heroBlock.classList.remove('is-in');
      delete heroBlock.dataset.revealed;
      /* Double-rAF: first frame paints the un-revealed state, second adds is-in
         so the CSS transitions actually fire. */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          heroBlock.classList.add('is-in');
        });
      });
    }

    /* ── Re-run any section {% javascript %} that relies on one-shot init ── */
    document.querySelectorAll('[data-cc-hero]').forEach(function (el) {
      delete el.dataset.megaBound;
    });

    /* Reset Lenis scroll to top */
    if (lenis) lenis.scrollTo(0, { immediate: true });
  };
})();
